import { List } from '@kerfjs/ui/list';
import { Text } from '@kerfjs/ui/text';
import type { SafeHtml } from 'kerfjs';

import type { LastPaidSnapshot } from '../../../../shared/contract/analysis.js';
import type { AnalysisReport } from '../../../data/api.js';
import { formatPercentText } from '../../../lib/currency-format.js';
import { CurrencyAmount } from '../../currency-amount.js';
import { VerdictChip } from '../../verdict-chip.js';

export function EvidencePricing({
  verdict,
  base,
}: {
  verdict: AnalysisReport['verdicts'][number];
  base: string;
}): SafeHtml {
  return (
    <List gap="xs">
      <Text>
        <VerdictChip verdict={verdict.verdict} /> {verdict.recommendedAction ?? 'No recommendation'}
        {verdict.confidence !== null ? ` · ${verdict.confidence} confidence` : null}
      </Text>
      <Text>
        Best quote:{' '}
        {verdict.bestQuotePrice === null ? (
          '—'
        ) : (
          <CurrencyAmount value={Number(verdict.bestQuotePrice)} currency={base} />
        )}{' '}
        · Benchmark range:{' '}
        {verdict.benchmarkLow === null || verdict.benchmarkHigh === null
          ? '—'
          : [
              <CurrencyAmount value={Number(verdict.benchmarkLow)} currency={base} />,
              '–',
              <CurrencyAmount value={Number(verdict.benchmarkHigh)} currency={base} />,
            ]}{' '}
        · Delta: {verdict.deltaPct === null ? '—' : formatPercentText(Number(verdict.deltaPct) / 100, { signed: true })}{' '}
        · Target:{' '}
        {verdict.targetPrice === null ? '—' : <CurrencyAmount value={Number(verdict.targetPrice)} currency={base} />}
      </Text>
      {verdict.lastPaidSnapshot != null ? <LastPaid paid={verdict.lastPaidSnapshot} /> : null}
      {verdict.bestQuoteInlandAdj !== null &&
      Number(verdict.bestQuoteInlandAdj) > 0 &&
      verdict.bestQuotePrice !== null ? (
        <Text tone="quiet" size="compact">
          Normalized to delivered-to-site: {verdict.bestQuoteBasis ?? 'ex-works'}{' '}
          <CurrencyAmount value={Number(verdict.bestQuotePrice) - Number(verdict.bestQuoteInlandAdj)} currency={base} />{' '}
          + <CurrencyAmount value={Number(verdict.bestQuoteInlandAdj)} currency={base} /> inland freight →{' '}
          <CurrencyAmount value={Number(verdict.bestQuotePrice)} currency={base} /> delivered.
        </Text>
      ) : null}
      {verdict.rationale !== null && verdict.rationale !== '' ? (
        <Text>{verdict.rationale}</Text>
      ) : (
        <Text tone="quiet">No rationale recorded.</Text>
      )}
      {verdict.caveats !== null && verdict.caveats.length > 0 ? (
        <ul class="evidence-caveats">
          {verdict.caveats.map((item, index) => (
            <li data-key={String(index)}>{item}</li>
          ))}
        </ul>
      ) : (
        <Text tone="quiet">No caveats recorded.</Text>
      )}
    </List>
  );
}

/**
 * The organization's last-paid purchase this analysis version used (HS2-Q7XA1M):
 * shown in its own currency, with its source row, and labelled as internal
 * history so it is never read as a market benchmark.
 */
function LastPaid({ paid }: { paid: LastPaidSnapshot }): SafeHtml {
  const where = `${paid.documentName} · ${paid.sheet === null ? '' : `${paid.sheet} `}row ${String(paid.sourceRow)}`;
  return (
    <List gap="2xs" className="evidence-last-paid">
      <Text>
        Last paid: <CurrencyAmount value={Number(paid.unitPrice)} currency={paid.currency} />
        {paid.unit === null ? null : ` per ${paid.unit}`} on {paid.purchasedOn}
      </Text>
      <Text tone="quiet" size="compact">
        {[where, paid.vendor].filter((part) => part !== null && part !== '').join(' · ')}. Your purchase history as
        recorded when this analysis ran, in its original currency; not a market benchmark.
      </Text>
    </List>
  );
}
